// VersionsBar — under-the-asset-header strip of pills, one per uploaded
// version of the active asset. Mirrors vanilla `renderVersionBar` +
// `versionPlay` + `versionEditNote` (legacy/index.html lines 10100-10167).
//
// Each pill is a two-row layout (~2× the original height per FB-r4):
//
//   Row 1 — controls + label
//     • a Play / Pause toggle that streams the version's COMPOSITE audio
//       (PreMixed if the version has it, else Voice-Only timeline WAV, else
//       fall back to the per-block MP3s in sequence) via the `/stream/...`
//       server endpoint
//     • an always-visible note icon — `ph-fill ph-chat-centered-text` when a
//       note exists, `ph-bold ph-chat-centered-text` otherwise. Click opens
//       `window.prompt()` to add/edit/clear (mirrors vanilla 10130 + 10152).
//     • the version label ("Version N") in semibold
//     • mode marker (TTS/STS chip) for custom assets only
//     • final-version star toggle
//
//   Row 2 — duration readouts
//     • Voice-Only Length — sum of voice-clip durations at upload time,
//       traffic-lighted vs `asset.targetDuration` (kept from the original
//       single-line layout, just relocated)
//     • File / Asset Length — actual rendered file length =
//       max(currentBoundary, voiceLength). Includes overlength when voice
//       extends past the boundary.
//
// A hover tooltip on the note icon surfaces the note text when present.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useSessionStore } from '@/stores/session';
import { useVideoStore } from '@/stores/video';
import { cancelUploadVersion, patchVersionNote, setFinalVersion, streamAudioUrl } from '@/lib/api';
import { isVersionCompleteConflict, refreshVersions } from '@/lib/cancelUpload';
import { buildAssetFolderName, buildFilePrefix, fileLangCode } from '@/lib/uploadFilenames';
import { assetGlobalIdx } from '@/lib/assetNumber';
import { isVersionPending, missingFiles } from '@/lib/versionStatus';
import { playFromUrl, type PlayHandle } from '@/lib/audioPlayback';
import { registerPlaybackStop, startPlayback, clearPlayback } from '@/stores/playback';
import { lastClipEndForAsset, resolveTimelineLength } from '@/lib/timelineLength';
import { isFlexAsset } from '@/lib/assetUi';
import { isStsMode } from '@/lib/assetMode';
import { useToast } from '@/components/ToastProvider';
import styles from './VersionsBar.module.css';

export function VersionsBar() {
  const currentAssetIdx = useSessionStore((s) => s.currentAssetIdx);
  const versionsMap = useSessionStore((s) => s.versions);
  const sessionFolder = useSessionStore((s) => s.sessionFolder);
  const toolId = useSessionStore((s) => s.toolId);
  const shortId = useSessionStore((s) => s.shortId);
  const updateVersionNote = useSessionStore((s) => s.updateVersionNote);
  const setVersionFinal = useSessionStore((s) => s.setVersionFinal);
  const removeVersion = useSessionStore((s) => s.removeVersion);
  // Subscribe to each slice individually (TTSView pattern) so a fresh
  // selectAllAssets-style merge can be memoized without subscribing to
  // an array that re-creates on every render.
  const configAssets = useSessionStore((s) => s.config?.assets);
  const customAssets = useSessionStore((s) => s.customAssets);
  const config = useSessionStore((s) => s.config);
  const asset = useMemo(() => {
    const cfg = configAssets ?? [];
    return [...cfg, ...customAssets][currentAssetIdx];
  }, [configAssets, customAssets, currentAssetIdx]);
  // Vanilla resolves the asset's _isCustom flag via getAllAssets() so the
  // folder name picks the "N - Custom - {title}" branch. Derive from the
  // local slices (predefined count + currentAssetIdx) instead of subscribing
  // to selectAllAssets — that selector returns a fresh array every call and
  // would trigger React's "getSnapshot should be cached" infinite loop guard.
  const predefinedCount = configAssets?.length ?? 0;
  const isCustom = currentAssetIdx >= predefinedCount;
  // Nummer in Ordner-/Dateinamen und Stream-URL: die globale Position aus
  // der Config (Audit 2026-09-24 H4), nicht der sprachgefilterte Index.
  const nameIdx = assetGlobalIdx(asset, currentAssetIdx);
  const toast = useToast();

  const versions = (asset && versionsMap[asset.id]) ?? [];
  const target = asset?.targetDuration ?? null;

  // File/Asset Length — total length of the actual uploaded file. Mirrors
  // the timeline's `boundary` (= `getAssetTimelineDuration`) which the
  // export pipeline uses as `ExportInput.totalDuration` when rendering the
  // `_TIMELINE-VOICE.wav` / `_TIMELINE-PREMIX.wav` payload.
  //
  // Overlength rule: when the version's cumulative voice duration exceeds
  // the current asset boundary, the rendered file extended past the boundary
  // (the timeline's `inOvertime` branch). We surface the larger of the two
  // so the pill reads the actual file length instead of clipping back to
  // the boundary — matches the "download overlength fix" semantics the user
  // already saw on the audio export side.
  const videoDurations = useVideoStore((s) => s.durations);
  const customDurations = useSessionStore((s) => s.customDurations);
  // Flex-Timeline (Multi-Lingual, Asset ohne Video — Spec 2026-09-21 § 6):
  // die Datei ist genau so lang wie der letzte Clip — dafür braucht der
  // Resolver die aktiven Blocks. Mit Video gilt die Videodauer wie im
  // Standard.
  const flex = isFlexAsset(config, asset);
  const activeBlocks = useSessionStore((s) => s.assetBlocks[s.currentAssetIdx]);
  // F2 (Spec 2026-09-21): `lastFavClipEnd` kennt nur TTS-Blocks und liefert
  // für STS-Assets immer 0 — flex-Länge fiel dadurch auf 0 zurück, der Badge
  // zeigte die Voice-Summe statt der gerenderten Länge. Gleiche Dispatch-
  // Regel wie `exportSnapshot.lastVoiceClipEnd` (`isStsMode`), dafür zusätzlich
  // die aktiven MasterClips abonnieren.
  const freeMode = useSessionStore((s) => s.freeMode[s.currentAssetIdx]);
  const isStsActive = isStsMode(asset?.assetType, freeMode as 'tts' | 'sts' | undefined);
  const activeMasterClips = useSessionStore((s) => s.stsAssets[s.currentAssetIdx]?.master);
  // Longest of all loaded beds (max 2) — same fallback semantics the
  // timeline boundary uses, so the version row's "this asset is X long"
  // hint matches what the talent sees on the timeline above.
  const bedDuration = useSessionStore((s) => {
    const beds = s.sfxBeds[s.currentAssetIdx] ?? [];
    if (beds.length === 0) return null;
    return beds.reduce((m, b) => Math.max(m, b.duration || 0), 0);
  });
  const assetBoundary = useMemo(() => {
    const length = resolveTimelineLength({
      flex,
      assetIdx: currentAssetIdx,
      videoDurations,
      targetDuration: target,
      customDurations,
      bedDuration,
      lastClipEnd: lastClipEndForAsset(isStsActive, activeBlocks ?? [], activeMasterClips ?? []),
    });
    return length.flex ? length.exportLength : length.boundary;
  }, [flex, currentAssetIdx, videoDurations, target, customDurations, bedDuration, activeBlocks, isStsActive, activeMasterClips]);
  const fileAssetLengthFor = (voiceLen: number): number =>
    Math.max(assetBoundary, voiceLen || 0);

  // Pick the best playback queue from a version's file list. Prefers the
  // PreMixed timeline WAV (full mix incl. video audio + SFX bed) when
  // present, then the Voice-Only timeline WAV (assembled timing). Falls
  // back to the per-block MP3 sequence for legacy versions uploaded before
  // commit ec98d50 / b4fd428 added the composite WAVs. Filters out plain-
  // text files (script.txt, params.txt, _NOTE.txt) so they never reach the
  // <audio> element.
  const pickPlaybackFiles = (files: string[]): string[] => {
    const mixed = files.find((f) => /_TIMELINE-PREMIX\.wav$/i.test(f));
    if (mixed) return [mixed];
    const voice = files.find((f) => /_TIMELINE-VOICE\.wav$/i.test(f));
    if (voice) return [voice];
    return files.filter((f) => /\.(mp3|wav|m4a|ogg)$/i.test(f));
  };

  const [playingId, setPlayingId] = useState<string | null>(null);
  // FB#2 — large composite WAVs (5-10MB PreMixed timeline) used to buffer
  // ~0.5s before HTMLAudio's `playing` event fired. The unified Web Audio
  // path fetches → decodes → plays, so the spinner now covers fetch+decode
  // and `onStarted` flips to the Pause icon at sample-accurate scheduling.
  // Cached buffers replay instantly on the second click.
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const handleRef = useRef<PlayHandle | null>(null);
  const fileQueueRef = useRef<string[]>([]);
  const fileIdxRef = useRef<number>(0);
  // Track the playId the running playback chain belongs to so async
  // `ended` callbacks for stale chains can short-circuit instead of
  // resuming after the user switched versions.
  const activePlayIdRef = useRef<string | null>(null);

  const stopActive = () => {
    const h = handleRef.current;
    if (h) {
      try { h.stop(); } catch { /* ignore */ }
      handleRef.current = null;
    }
  };

  // Register in the single-source playback mutex as the 'version-bar' kind so
  // starting a Timeline preview / chip / pipeline — or opening/submitting the
  // Upload modal (stopAllPlayback) — silences a playing version pill, and vice
  // versa. Without this, a version and the timeline could play over each other,
  // and version audio kept playing behind the Upload modal.
  useEffect(() => {
    return registerPlaybackStop('version-bar', () => {
      stopActive();
      activePlayIdRef.current = null;
      setPlayingId(null);
      setLoadingId(null);
    });
  }, []);

  // Cleanup on unmount.
  useEffect(() => {
    return () => {
      stopActive();
      activePlayIdRef.current = null;
      clearPlayback('version-bar');
    };
  }, []);

  // Reset playback when switching assets.
  useEffect(() => {
    stopActive();
    activePlayIdRef.current = null;
    setPlayingId(null);
    setLoadingId(null);
    clearPlayback('version-bar');
  }, [currentAssetIdx]);

  if (!asset || versions.length === 0) return null;

  const playVersion = (version: number, files: string[]) => {
    const id = `${asset.id}:${version}`;
    // Toggle: clicking the currently-playing version stops. Also covers the
    // loading-but-not-yet-playing state — a second click cancels the pending
    // start cleanly so the icon falls back to Play.
    if (playingId === id || loadingId === id) {
      stopActive();
      activePlayIdRef.current = null;
      setPlayingId(null);
      setLoadingId(null);
      clearPlayback('version-bar');
      return;
    }
    // Stop any other version.
    stopActive();
    if (!toolId || !sessionFolder || files.length === 0) return;
    // Claim the playback mutex — silences a running timeline preview / chip /
    // pipeline before this version starts.
    startPlayback('version-bar');
    fileQueueRef.current = files.slice();
    fileIdxRef.current = 0;
    activePlayIdRef.current = id;
    // Enter the LOADING state immediately — covers fetch + decodeAudioData
    // for the first play of a version. Cached buffers (decoded once and
    // memoised by stream URL) skip straight to onStarted on subsequent plays
    // and the spinner is barely visible.
    setPlayingId(null);
    setLoadingId(id);

    const playNext = () => {
      // Stale guard: another version started or unmount/asset-change cleared it.
      if (activePlayIdRef.current !== id) return;
      if (fileIdxRef.current >= fileQueueRef.current.length) {
        activePlayIdRef.current = null;
        handleRef.current = null;
        setPlayingId(null);
        setLoadingId(null);
        clearPlayback('version-bar');
        return;
      }
      const filename = fileQueueRef.current[fileIdxRef.current];
      const url = streamAudioUrl(toolId, sessionFolder, nameIdx, version, filename, asset?.id);

      void playFromUrl(url, {
        // Route through voice-bus + bypassMixer so:
        //   • Safari MediaStream tap applies (ac.destination silently
        //     degrades under large-blob memory pressure on Safari, which
        //     is exactly when a version replay matters most). Without this
        //     versions are silent on Safari while chip / timeline plays
        //     remain audible.
        //   • bypassMixer keeps version playback at unity reference level
        //     regardless of the Voice fader / mute state — preserves the
        //     pre-2026-04-30 'direct' routing semantics where versions
        //     played independent of the mixer.
        routing: 'voice-bus',
        bypassMixer: true,
        // Stay in loading state during fetch + decode. onStarted flips to
        // PLAYING the moment the AudioBufferSourceNode actually starts —
        // sample-accurate, so no inter-file silence on legacy multi-file
        // queues either.
        onStarted: () => {
          if (activePlayIdRef.current !== id) return;
          setLoadingId(null);
          setPlayingId(id);
        },
        onEnded: () => {
          // Stale guard: a different version took over.
          if (activePlayIdRef.current !== id) return;
          fileIdxRef.current += 1;
          // For multi-file legacy queues, briefly drop back to loading
          // while the next file fetches/decodes. The next file's onStarted
          // will flip it back to PLAYING.
          if (fileIdxRef.current < fileQueueRef.current.length) {
            setPlayingId(null);
            setLoadingId(id);
          }
          playNext();
        },
      })
        .then((handle) => {
          // Late-arriving handle for a stale request (asset switched mid-
          // fetch). Stop it before assigning so we don't leak audio.
          if (activePlayIdRef.current !== id) {
            try { handle.stop(); } catch { /* ignore */ }
            return;
          }
          handleRef.current = handle;
        })
        .catch(() => {
          if (activePlayIdRef.current === id) {
            activePlayIdRef.current = null;
            handleRef.current = null;
            setPlayingId(null);
            setLoadingId(null);
            clearPlayback('version-bar');
          }
        });
    };
    playNext();
  };

  // Vanilla `versionEditNote` (legacy/index.html:10147) — `prompt()` opens
  // with the current note prefilled, null = cancel, empty string = clear.
  // Optimistic store update + revert on PATCH failure with toast.
  const editNote = async (version: number, currentNote: string | null) => {
    if (!toolId || !sessionFolder || !asset) return;
    const input = window.prompt(`Note for Version ${version}:`, currentNote ?? '');
    if (input === null) return; // cancelled
    const next = input.trim() || null;
    if (next === (currentNote ?? null)) return; // no change
    // Optimistic update
    updateVersionNote(asset.id, version, next);
    try {
      // Match vanilla payload (legacy/index.html:10162) so the server also
      // rewrites `{prefix}_NOTE.txt` on WebDAV alongside the KV update.
      const langCode = fileLangCode(config);
      const assetFolder = buildAssetFolderName({
        assetIdx: nameIdx,
        title: asset.title,
        isCustom,
      });
      const noteFilename =
        buildFilePrefix({
          langCode,
          shortId: shortId || 'xxxx',
          assetIdx: nameIdx,
          assetTitle: asset.title,
          version,
        }) + '_NOTE.txt';
      await patchVersionNote(toolId, sessionFolder, {
        assetId: asset.id,
        version,
        note: next,
        assetIdx: nameIdx,
        assetFolder,
        noteFilename,
      });
    } catch (err) {
      // Revert on failure.
      updateVersionNote(asset.id, version, currentNote);
      toast.show('Could not save note');
      console.warn('patchVersionNote failed', err);
    }
  };

  // Toggle the FINAL-VERSION marker for this asset. At most one version per
  // asset can be flagged — clicking a different pill auto-unsets the previous
  // one (server enforces the same invariant in KV; the empty FINAL-VERSION
  // file lives inside the matching Version N/ folder so it travels with the
  // asset on rename and disappears on delete). Optimistic update + revert.
  const toggleFinal = async (clickedVersion: number) => {
    if (!toolId || !sessionFolder || !asset) return;
    const currentFinal = versions.find((v) => v.isFinal);
    const isClickingActiveFinal =
      currentFinal != null && currentFinal.version === clickedVersion;
    const nextVersion: number | null = isClickingActiveFinal ? null : clickedVersion;
    const prevVersion = currentFinal?.version ?? null;
    // Snapshot for rollback.
    const prevSnapshot = currentFinal?.version ?? null;
    const assetFolder = buildAssetFolderName({
      assetIdx: nameIdx,
      title: asset.title,
      isCustom,
    });
    // Optimistic.
    setVersionFinal(asset.id, nextVersion);
    try {
      await setFinalVersion(toolId, sessionFolder, {
        assetId: asset.id,
        assetIdx: nameIdx,
        version: nextVersion,
        assetFolder,
        prevVersion,
        prevAssetFolder: assetFolder,
      });
    } catch (err) {
      // Revert: restore the previous "final" state.
      setVersionFinal(asset.id, prevSnapshot);
      toast.show('Could not update final version');
      console.warn('setFinalVersion failed', err);
    }
  };

  // Halbe Version (Audit M15): der Upload hat die Nummer reserviert, aber
  // nicht alle angekündigten Dateien sind angekommen (Tab zu, Crash, Abbruch
  // ohne Aufräumen). Kein Play, keine Notiz, kein Stern — nur der Hinweis und
  // ein Aufräumen-Button (idempotent: Ordner + Record weg).
  const removePending = async (version: number) => {
    if (!toolId || !sessionFolder || !asset) return;
    const ok = window.confirm(`Remove the incomplete upload of Version ${version}? Files already in TRO Cloud will be deleted.`);
    if (!ok) return;
    try {
      await cancelUploadVersion(toolId, {
        sessionFolder,
        assetFolderName: buildAssetFolderName({ assetIdx: nameIdx, title: asset.title, isCustom }),
        version,
        assetId: asset.id,
      });
      removeVersion(asset.id, version);
    } catch (err) {
      // 409: der Dienst entfernt nur halbe Versionen (Integrations-Review
      // 2026-09-24) — die Version ist inzwischen vollständig und bleibt.
      if (isVersionCompleteConflict(err)) {
        // Fertigen Stand nachladen, statt zum Reload aufzufordern.
        const refreshed = await refreshVersions(toolId, sessionFolder);
        toast.show(refreshed
          ? 'This version has finished uploading in the meantime.'
          : 'This version has finished uploading in the meantime — reload the page to see it.');
        return;
      }
      toast.show('Could not remove the incomplete upload');
      console.warn('cancelUploadVersion failed', err);
    }
  };

  return (
    <div className={styles.bar}>
      <span className={styles.label}>Uploaded Versions</span>
      <div className={styles.pills}>
        {versions.map((v) => {
          if (isVersionPending(v)) {
            const missing = missingFiles(v);
            return (
              <div key={v.version} className={styles.pill}>
                <div className={styles.pillRow}>
                  <i className={`ph-fill ph-warning ${styles.pendingIcon}`} aria-hidden="true" />
                  <span className={styles.versionLabel}>Version {v.version}</span>
                  <span
                    className={`${styles.badge} ${styles.badgeWarn}`}
                    title={missing.length > 0 ? `Missing: ${missing.join(', ')}` : 'Upload did not finish'}
                  >
                    incomplete
                  </span>
                  <button
                    type="button"
                    className={styles.iconBtn}
                    onClick={(e) => {
                      e.stopPropagation();
                      void removePending(v.version);
                    }}
                    title="Remove incomplete upload"
                    aria-label="Remove incomplete upload"
                  >
                    <i className="ph-bold ph-trash" aria-hidden="true" />
                  </button>
                </div>
                <div className={`${styles.pillRow} ${styles.pillRowMeta}`}>
                  <span className={styles.metaItem}>
                    <span className={styles.metaLabel}>Upload did not finish — remove it and upload again</span>
                  </span>
                </div>
              </div>
            );
          }
          const playId = `${asset.id}:${v.version}`;
          const isPlaying = playingId === playId;
          const isLoading = loadingId === playId;
          const voiceBadge = renderDurationBadge(v.duration, target);
          const fileBadge = renderFileLengthBadge(
            fileAssetLengthFor(v.duration),
          );
          const noteTitle = v.note ? 'Edit note' : 'Add note';
          // Tri-state icon: loading > playing > idle. Title/aria mirror the
          // semantics so screen readers surface the buffer state.
          const iconClass = isLoading
            ? `ph-bold ph-circle-notch ${styles.spin}`
            : isPlaying
              ? 'ph-fill ph-pause'
              : 'ph-fill ph-play';
          const btnTitle = isLoading
            ? 'Loading…'
            : isPlaying
              ? 'Pause'
              : 'Play';
          return (
            <div key={v.version} className={styles.pill}>
              {/* Row 1 — controls + label + mode marker + final star.
                  Stays in the original left-to-right reading order so
                  click targets don't move; the duration values were just
                  pushed down to row 2 to make room for two of them. */}
              <div className={styles.pillRow}>
                <button
                  type="button"
                  className={styles.iconBtn}
                  onClick={() => playVersion(v.version, pickPlaybackFiles(v.files))}
                  title={btnTitle}
                  aria-label={btnTitle}
                >
                  <i
                    className={iconClass}
                    aria-hidden="true"
                  />
                </button>
                <button
                  type="button"
                  className={`${styles.iconBtn} ${styles.noteBtn}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    void editNote(v.version, v.note ?? null);
                  }}
                  title={noteTitle}
                  aria-label={noteTitle}
                >
                  <i
                    className={
                      v.note
                        ? 'ph-fill ph-chat-centered-text'
                        : 'ph-bold ph-chat-centered-text'
                    }
                    aria-hidden="true"
                    style={{ fontSize: 15 }}
                  />
                  {v.note && <span className={styles.tooltip}>{v.note}</span>}
                </button>
                <span className={styles.versionLabel}>Version {v.version}</span>
                {/* Mode marker — only shown on custom-asset pills, and only
                    when the version actually carries `mode`. Legacy versions
                    uploaded before the field existed have `mode === undefined`
                    and silently render no chip (graceful migration). The asset
                    switcher already differentiates predefined assets by their
                    fixed type, so we omit the chip there as redundant info. */}
                {isCustom && v.mode && (
                  <span
                    className={styles.modeChip}
                    title={v.mode === 'tts' ? 'Text-to-Speech' : 'Speech-to-Speech'}
                  >
                    {v.mode.toUpperCase()}
                  </span>
                )}
                <button
                  type="button"
                  className={`${styles.iconBtn} ${styles.starBtn} ${
                    v.isFinal ? styles.starBtnActive : ''
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    void toggleFinal(v.version);
                  }}
                  title={v.isFinal ? 'Unmark final version' : 'Mark as final version'}
                  aria-label={v.isFinal ? 'Unmark final version' : 'Mark as final version'}
                  aria-pressed={!!v.isFinal}
                >
                  <i
                    className={v.isFinal ? 'ph-fill ph-star' : 'ph-bold ph-star'}
                    aria-hidden="true"
                  />
                </button>
              </div>
              {/* Row 2 — two duration readouts.
                  • Voice-Only Length: sum of voice-clip durations as
                    captured at upload time (`UploadVersion.duration`),
                    traffic-lighted against `targetDuration`.
                  • File/Asset Length: rendered file length =
                    max(currentBoundary, voiceLength). Bumps up to the
                    voice length when voice extends past the boundary
                    (overlength) — same logic the audio export uses. */}
              <div className={`${styles.pillRow} ${styles.pillRowMeta}`}>
                <span className={styles.metaItem}>
                  <span className={styles.metaLabel}>Voice-Only</span>
                  {voiceBadge ?? <span className={styles.metaDash}>—</span>}
                </span>
                <span className={styles.metaItem}>
                  <span className={styles.metaLabel}>File / Asset</span>
                  {fileBadge}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function renderDurationBadge(
  duration: number | null | undefined,
  target: number | null,
) {
  if (!duration || duration <= 0) return null;
  let cls = styles.badgeOk;
  if (target && duration > target * 1.1) cls = styles.badgeError;
  else if (target && duration > target) cls = styles.badgeWarn;
  return <span className={`${styles.badge} ${cls}`}>{duration.toFixed(1)}s</span>;
}

// File/Asset length is the actual file's runtime — there's no comparison
// target (the file IS the target length). Render in the neutral pill style
// so it doesn't compete visually with the traffic-light Voice-Only badge.
function renderFileLengthBadge(duration: number | null | undefined) {
  if (!duration || duration <= 0) return <span className={styles.metaDash}>—</span>;
  return (
    <span className={`${styles.badge} ${styles.badgeNeutral}`}>
      {duration.toFixed(1)}s
    </span>
  );
}
